import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import '../users/userform.css';
import '../tasks/tasklist.css';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../services/AxiosInstance";
import { FiArrowLeft } from 'react-icons/fi';
import Select from 'react-select';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { buildHierarchicalClassificationOptions, sortCategoriesHierarchically } from '../../utils/classificationHierarchy';
import { getCurrentUserRole, isBranchAdmin, isDepartmentAdmin, isSubDepartmentAdmin, ROLES, normalizeRole, canEditDeleteClassification } from '../../utils/roleAccess';
import { useDocumentAccess } from '../../hooks/useDocumentAccess';

const AddDocumentCategory = () => {
  const { hasAccess, loading: accessLoading } = useDocumentAccess();
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    parent_id: null,
    is_favorite: false,
    shared_branches: [],
    shared_departments: []
  });
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [parentClassificationOptions, setParentClassificationOptions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [showBranchField, setShowBranchField] = useState(true);
  const [showDepartmentField, setShowDepartmentField] = useState(true);
  const [currentUserBranchId, setCurrentUserBranchId] = useState(null);
  const [currentUserDepartmentId, setCurrentUserDepartmentId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#667eea' : '#e9ecef',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(102, 126, 234, 0.25)' : 'none',
      minHeight: '38px',
      '&:hover': {
        borderColor: '#ced4da'
      },
      height: '49px',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#667eea' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#495057',
      '&:active': {
        backgroundColor: '#667eea'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6c757d'
    })
  };

  // Fetch available categories for parent selection
  useEffect(() => {
    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const userData = JSON.parse(localStorage.getItem("user"));
        const corporateId = userData?.corporateId || userData?.id;
        
        const response = await axiosInstance.get(`/document-categories?corporate_id=${corporateId}`);
        const data = response.data;
        
        if (response.status === 200) {
          const categories = data.success ? data.data : (data.data || data || []);
          // Filter out the current classification being edited (if editing)
          const filteredClassifications = editId 
            ? categories.filter(cat => cat.id !== parseInt(editId))
            : categories;
          setAvailableCategories(filteredClassifications);
          
          // Build hierarchical options for parent selection (tree-style)
          const sortedCategories = sortCategoriesHierarchically(filteredClassifications);
          const options = buildHierarchicalClassificationOptions(sortedCategories, {
            excludeId: editId ? parseInt(editId) : null,
            showHierarchy: true,
            treeStyle: true
          });
          
          // Add "None (Top Level Classification)" option at the beginning
          setParentClassificationOptions([
            { value: null, label: 'None (Top Level Classification)', originalLabel: 'None (Top Level Classification)' },
            ...options
          ]);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoadingCategories(false);
      }
    };
    
    loadCategories();
  }, [editId]);


  // Prefill parent classification when parent_id is in URL and categories are loaded
  useEffect(() => {
    const parentIdFromQuery = searchParams.get("parent_id");
    
    // Only prefill if:
    // 1. parent_id is in URL
    // 2. Not editing an existing classification
    // 3. Categories are loaded
    // 4. Parent ID hasn't been set yet or doesn't match
    if (parentIdFromQuery && !editId && availableCategories.length > 0) {
      const parentId = parseInt(parentIdFromQuery);
      
      // Verify the parent category exists in available categories
      const parentExists = availableCategories.find(cat => cat.id === parentId);
      
      if (parentExists && formData.parent_id !== parentId) {
        setFormData(prev => ({
          ...prev,
          parent_id: parentId
        }));
      }
    }
  }, [searchParams, availableCategories, editId, formData.parent_id]);

  // Initialize role-based access control
  useEffect(() => {
    const initializeRoleBasedAccess = async () => {
      try {
        const role = getCurrentUserRole();
        const userData = JSON.parse(localStorage.getItem("user"));
        const userId = userData?.id;

        if (!userId) return;

        // Get current user's branch and department details
        try {
          const userDetailsRes = await axiosInstance.get(`/auth/details/${userId}`);
          const userDetails = userDetailsRes.data;
          const branchId = userDetails?.Branch?.id || userDetails?.BranchId;
          const departmentId = userDetails?.Department?.id || userDetails?.DepartmentId;

          setCurrentUserBranchId(branchId);
          setCurrentUserDepartmentId(departmentId);

          const normalizedRole = normalizeRole(role);
          
          // Normal user: hide both fields (can only create private classifications)
          if (normalizedRole === ROLES.USER) {
            setShowBranchField(false);
            setShowDepartmentField(false);
          }
          // Sub department admin: hide both fields
          else if (isSubDepartmentAdmin(role)) {
            setShowBranchField(false);
            setShowDepartmentField(false);
          }
          // Department admin: hide branch field
          else if (isDepartmentAdmin(role)) {
            setShowBranchField(false);
            setShowDepartmentField(true);
          }
          // Branch admin: show both fields but will filter them
          else if (isBranchAdmin(role)) {
            setShowBranchField(true);
            setShowDepartmentField(true);
          }
          // Corporate admin: show all fields
          else {
            setShowBranchField(true);
            setShowDepartmentField(true);
          }
        } catch (err) {
          console.error('Error fetching current user details for role restrictions:', err);
        }
      } catch (err) {
        console.error('Error initializing role-based access:', err);
      }
    };

    initializeRoleBasedAccess();
  }, []);

  // Fetch branches and departments
  useEffect(() => {
    const loadBranchesAndDepartments = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("user"));
        const corporateId = userData?.corporateId || userData?.id;
        const role = getCurrentUserRole();
        
        setLoadingBranches(true);
        const branchesResponse = await axiosInstance.get(`/settings/branches?corporate_id=${corporateId}`);
        if (branchesResponse.status === 200) {
          const branchesData = branchesResponse.data.success ? branchesResponse.data.data : (branchesResponse.data.data || branchesResponse.data || []);
          const allBranches = Array.isArray(branchesData) ? branchesData : [];
          setBranches(allBranches);

          // Filter branches based on role
          if (isBranchAdmin(role) && currentUserBranchId) {
            // Branch admin: only show their branch
            const filtered = allBranches.filter(branch => branch.id === currentUserBranchId);
            setFilteredBranches(filtered);
          } else {
            // Corporate admin or others: show all branches
            setFilteredBranches(allBranches);
          }
        }
        setLoadingBranches(false);

        setLoadingDepartments(true);
        const departmentsResponse = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}`);
        if (departmentsResponse.status === 200) {
          const departmentsData = departmentsResponse.data.success ? departmentsResponse.data.data : (departmentsResponse.data.data || departmentsResponse.data || []);
          const allDepartments = Array.isArray(departmentsData) ? departmentsData : [];
          setDepartments(allDepartments);

          // Filter departments based on role
          if (isDepartmentAdmin(role) && currentUserDepartmentId) {
            // Department admin: only show their department
            const filtered = allDepartments.filter(dept => dept.id === currentUserDepartmentId);
            setFilteredDepartments(filtered);
          } else if (isBranchAdmin(role) && currentUserBranchId) {
            // Branch admin: only show departments under their branch
            const filtered = allDepartments.filter(dept => dept.branch_id === currentUserBranchId);
            setFilteredDepartments(filtered);
          } else {
            // Corporate admin or others: show all departments
            setFilteredDepartments(allDepartments);
          }
        }
        setLoadingDepartments(false);
      } catch (error) {
        console.error("Error fetching branches/departments:", error);
        setLoadingBranches(false);
        setLoadingDepartments(false);
      }
    };
    
    loadBranchesAndDepartments();
  }, [currentUserBranchId, currentUserDepartmentId]);

  // Check for ID param in URL (for edit) - supports both path params and query params
  useEffect(() => {
    // Check path params first (e.g., /documents/editDocumentCategory/:id)
    const idFromPath = params.id;
    // Check query params as fallback (e.g., /documents/addDocumentCategory?id=123)
    const idFromQuery = searchParams.get("id");
    const id = idFromPath || idFromQuery;
    
    if (id) {
      setEditId(id);
      setFetching(true);
      axiosInstance.get(`/document-categories/${id}`)
        .then(async res => {
          const cat = res.data.data || res.data;
          
          // Check if user has permission to edit this classification
          const userData = JSON.parse(localStorage.getItem("user"));
          const role = getCurrentUserRole();
          const userId = userData?.id;
          
          // Get user's branch and department IDs
          let userBranchId = null;
          let userDepartmentId = null;
          let departmentIdsUnderBranch = [];
          if (userId) {
            try {
              const userDetailsRes = await axiosInstance.get(`/auth/details/${userId}`);
              const userDetails = userDetailsRes.data;
              userBranchId = userDetails?.Branch?.id || userDetails?.BranchId;
              userDepartmentId = userDetails?.Department?.id || userDetails?.DepartmentId;

              // If user is branch admin, fetch all departments under their branch
              if (userBranchId && isBranchAdmin(role)) {
                try {
                  const corporateId = userData?.corporateId || userData?.id;
                  const departmentsRes = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}&branch_id=${userBranchId}`);
                  const departmentsData = departmentsRes.data;
                  const departments = departmentsData.data || departmentsData || [];
                  departmentIdsUnderBranch = departments.map(dept => dept.id).filter(Boolean);
                } catch (err) {
                  console.error('Error fetching departments under branch for permission check:', err);
                }
              }
            } catch (err) {
              console.error('Error fetching user details for permission check:', err);
            }
          }
          
          // Check permission
          const canEdit = canEditDeleteClassification(cat, role, userBranchId, userDepartmentId, userId, departmentIdsUnderBranch);
          
          if (!canEdit) {
            toast.error("You don't have permission to edit this classification");
            navigate('/documents/documentClassifications');
            return;
          }
          
          // Normalize shared_branches and shared_departments to arrays of IDs
          const normalizeIds = (data) => {
            if (!data || !Array.isArray(data)) return [];
            // If array contains objects with id property, extract IDs
            if (data.length > 0 && typeof data[0] === 'object' && data[0] !== null && 'id' in data[0]) {
              return data.map(item => typeof item.id === 'number' ? item.id : parseInt(item.id)).filter(id => !isNaN(id));
            }
            // If array contains IDs (numbers or strings), convert to numbers
            return data.map(id => typeof id === 'number' ? id : parseInt(id)).filter(id => !isNaN(id));
          };

          const sharedBranchIds = normalizeIds(cat.shared_with_branches || cat.shared_branch_ids || cat.branch_ids || []);
          const sharedDepartmentIds = normalizeIds(cat.shared_with_departments || cat.shared_department_ids || cat.department_ids || []);

          setFormData({
            name: cat.name || "",
            parent_id: cat.parent_id || null,
            is_favorite: cat.is_favorite || false,
            shared_branches: sharedBranchIds,
            shared_departments: sharedDepartmentIds
          });
        })
        .catch((err) => {
          toast.error("Failed to load classification: " + (err.response?.data?.message || err.message));
          navigate('/documents/documentClassifications');
        })
        .finally(() => setFetching(false));
    }
  }, [params.id, searchParams, navigate]);

  // Filter pre-selected branches and departments in edit mode based on role restrictions
  useEffect(() => {
    if (editId && filteredBranches.length > 0 && filteredDepartments.length > 0) {
      const role = getCurrentUserRole();
      
      // Filter shared_branches to only include allowed branches
      if (isBranchAdmin(role) || isDepartmentAdmin(role) || isSubDepartmentAdmin(role)) {
        const allowedBranchIds = filteredBranches.map(b => b.id);
        const filteredBranchIds = (formData.shared_branches || []).filter(branchId => 
          allowedBranchIds.includes(branchId)
        );
        
        // Filter shared_departments to only include allowed departments
        const allowedDepartmentIds = filteredDepartments.map(d => d.id);
        const filteredDepartmentIds = (formData.shared_departments || []).filter(deptId => 
          allowedDepartmentIds.includes(deptId)
        );
        
        // Update form data if filtering changed the values
        if (filteredBranchIds.length !== (formData.shared_branches || []).length || 
            filteredDepartmentIds.length !== (formData.shared_departments || []).length) {
          setFormData(prev => ({
            ...prev,
            shared_branches: filteredBranchIds,
            shared_departments: filteredDepartmentIds
          }));
        }
      }
    }
  }, [editId, filteredBranches, filteredDepartments, formData.shared_branches, formData.shared_departments]);

  // Prefill parent classification when parent_id is in URL and categories are loaded
  useEffect(() => {
    const parentIdFromQuery = searchParams.get("parent_id");
    
    // Only prefill if:
    // 1. parent_id is in URL query params
    // 2. Not editing an existing classification  
    // 3. Categories are loaded (not loading and have data)
    // 4. Parent ID hasn't been set yet or doesn't match
    if (parentIdFromQuery && !editId && !loadingCategories && availableCategories.length > 0) {
      const parentId = parseInt(parentIdFromQuery);
      
      // Verify the parent category exists in available categories
      const parentExists = availableCategories.find(cat => cat.id === parentId);
      
      if (parentExists) {
        // Use functional update to avoid dependency on formData.parent_id
        setFormData(prev => {
          // Only update if parent_id is different
          if (prev.parent_id !== parentId) {
            return {
              ...prev,
              parent_id: parentId
            };
          }
          return prev;
        });
      }
    }
  }, [searchParams, availableCategories, editId, loadingCategories]);

  // Helper function to check if user came from classification list
  const cameFromClassificationList = () => {
    // Check location state (passed during navigation from classification list)
    if (location.state?.from === 'classificationList') {
      return true;
    }
    
    return false;
  };

  // Helper function to safely navigate back, avoiding login page
  const navigateBackSafely = () => {
    // Check if there's history to go back to
    if (window.history.length > 1) {
      navigate(-1);
      
      // Set a timeout to check if we ended up on login page
      setTimeout(() => {
        if (window.location.pathname.includes('/login')) {
          navigate('/documents/documentClassifications');
        }
      }, 100);
    } else {
      // No history, navigate to classification list as fallback
      navigate('/documents/documentClassifications');
    }
  };

  const handleChange = (e, type) => {
    if (type === "classification") {
      setFormData({ ...formData, name: e.target.value });
      if (errors.name) {
        const { name, ...rest } = errors;
        setErrors(rest);
      }
    } else if (type === "parent_id") {
      const parentId = e.target.value === "" ? null : parseInt(e.target.value);
      setFormData({ ...formData, parent_id: parentId });
      if (errors.parent_id) {
        const { parent_id, ...rest } = errors;
        setErrors(rest);
      }
    } else if (type === "is_favorite") {
      setFormData({ ...formData, is_favorite: e.target.checked });
    }
  };

  const handleBranchesChange = (selectedOptions) => {
    const branchIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setFormData({ ...formData, shared_branches: branchIds });
  };

  const handleDepartmentsChange = (selectedOptions) => {
    const departmentIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setFormData({ ...formData, shared_departments: departmentIds });
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Document Classification name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validate()) return;

    setLoading(true);
    setIsSubmitting(true);

    const payload = {
      name: formData.name,
      parent_id: formData.parent_id || null,
      is_favorite: formData.is_favorite || false,
      branch_ids: formData.shared_branches && formData.shared_branches.length > 0 ? formData.shared_branches : [],
      department_ids: formData.shared_departments && formData.shared_departments.length > 0 ? formData.shared_departments : []
    };
    
    try {
      let response;
      if (editId) {
        response = await axiosInstance.put(`/document-categories/${editId}`, payload);
      } else {
        response = await axiosInstance.post(`/document-categories`, payload);
      }

      if (response.data && response.data.success) {
        notifyTopRight(`✅ Document Classification ${editId ? "updated" : "created"} successfully!`);
        
        setTimeout(() => {
          setIsSubmitting(false);
          
          // If editing, navigate back safely (same as before)
          if (editId) {
            navigateBackSafely();
          } else {
            // For new classification, check where user came from
            const fromClassificationList = cameFromClassificationList();
            
            if (fromClassificationList) {
              // If came from classification list, redirect back there
              navigate('/documents/documentClassifications');
            } else {
              // Otherwise, navigate to the newly created classification folder
              // Extract classification ID from response
              const createdClassificationId = response.data?.data?.id || response.data?.id;
              
              if (createdClassificationId) {
                navigate(`/documents/folder/${createdClassificationId}`);
              } else {
                // Fallback: navigate back safely if ID not available
                navigateBackSafely();
              }
            }
          }
        }, 2000);
      } else {
        throw new Error(response.data?.message || "Unknown error");
      }

    } catch (error) {
      console.error('Submission error:', error);
      if (error.response?.status === 409) {
        notifyError('Document Classification already exists');
      } else {
        notifyError(error.response?.data?.message || error.response?.data?.error || `Failed to ${editId ? "update" : "create"} Document Classification`);
      }
      setIsSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

 

  const notifyTopRight = (data) => {
    toast.success(`✔️ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const notifyError = (data) => {
    toast.error(`❌ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  // Show loading while checking access
  if (accessLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Don't render if no access (will redirect)
  if (!hasAccess) {
    return null;
  }

  if (fetching) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        className="back-button"
        onClick={() => {
          if (window.history.length > 2) {
            navigate(-1);
          } else {
            navigate('/documents/documentClassifications');
          }
        }}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>
      <div className="row" style={{ marginBottom: '0px !important' }}>
        <ToastContainer />
        <div className="col-xl-12 col-xxl-12">
          <div className="card">
          <div className="card-body">
              <section>
                <div className="row">
                  <div className="col-lg-6" style={{ marginBottom: '1rem' }}>
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                      <label className="text-label">Document Classification*</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter document classification"
                        value={formData.name}
                        onChange={(e) => handleChange(e, "classification")}
                      />
                      {errors.name && <div className="text-danger" style={{ marginTop: '0.25rem' }}>{errors.name}</div>}
                    </div>
                  </div>
                  <div className="col-lg-6" style={{ marginBottom: '1rem' }}>
                    <div className="form-group" style={{ marginBottom: '1rem' }}>
                      <label className="text-label">Parent Classification (Optional)</label>
                      <Select
                        options={parentClassificationOptions}
                        value={(() => {
                          const selectedOption = parentClassificationOptions.find(opt => opt.value === formData.parent_id) || (parentClassificationOptions.length > 0 ? parentClassificationOptions[0] : null);
                          if (selectedOption) {
                            return { ...selectedOption, label: selectedOption.originalLabel || selectedOption.label };
                          }
                          return null;
                        })()}
                        onChange={(option) => {
                          const parentId = option && option.value !== null ? option.value : null;
                          setFormData({ ...formData, parent_id: parentId });
                          if (errors.parent_id) {
                            const { parent_id, ...rest } = errors;
                            setErrors(rest);
                          }
                        }}
                        isDisabled={loadingCategories}
                        isClearable={false}
                        isSearchable
                        placeholder="Select parent classification"
                        isLoading={loadingCategories}
                        getOptionLabel={(option) => option.originalLabel || option.label}
                        formatOptionLabel={({ label }) => (
                          <div style={{ whiteSpace: 'pre' }}>{label}</div>
                        )}
                        styles={customStyles}
                      />
                      {errors.parent_id && <div className="text-danger" style={{ marginTop: '0.25rem' }}>{errors.parent_id}</div>}
                      {formData.parent_id && (
                        <small className="text-muted" style={{ marginTop: '0.25rem', display: 'block' }}>
                          This classification will be created as a sub-classification
                        </small>
                      )}
                    </div>
                  </div>
                </div>

                {/* Favorite Option */}
                <div className="row">
                  <div className="col-lg-12" style={{ marginBottom: '1rem' }}>
                    <div className="form-group" style={{ marginBottom: '1rem', marginTop: '1.5rem' }}>
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          id="is_favorite"
                          checked={formData.is_favorite}
                          onChange={(e) => handleChange(e, "is_favorite")}
                          className="checkbox-input"
                        />
                        <span>
                          Favorite
                          <i 
                            className="fas fa-info-circle ms-2 text-muted" 
                            style={{ fontSize: '0.875rem' }}
                            title="Favorited classifications will appear at the top of the list"
                          ></i>
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Share with Branches/Departments */}
                <div className="row">
                  {showBranchField && (
                    <div className="col-lg-6" style={{ marginBottom: '1rem' }}>
                      <div className="form-group" style={{ marginBottom: '1rem' }}>
                        <label className="text-label">Share with Branches</label>
                        <Select
                          isMulti
                          options={filteredBranches.map((branch) => ({
                            value: branch.id,
                            label: branch.name,
                            data: branch,
                          }))}
                          value={filteredBranches.filter(b => (formData.shared_branches || []).includes(b.id)).map(b => ({
                            value: b.id,
                            label: b.name,
                            data: b
                          }))}
                          onChange={handleBranchesChange}
                          isClearable
                          placeholder="Select Branches"
                          isLoading={loadingBranches}
                          styles={customStyles}
                        />
                        <small className="text-muted" style={{ marginTop: '0.25rem', display: 'block' }}>
                          If none selected, only you will see this classification
                        </small>
                      </div>
                    </div>
                  )}
                  {showDepartmentField && (
                    <div className="col-lg-6" style={{ marginBottom: '1rem' }}>
                      <div className="form-group" style={{ marginBottom: '1rem' }}>
                        <label className="text-label">Share with Departments</label>
                        <Select
                          isMulti
                          options={filteredDepartments.map((dept) => ({
                            value: dept.id,
                            label: dept.branch_name ? `${dept.name} (${dept.branch_name})` : dept.name,
                            data: dept,
                          }))}
                          value={filteredDepartments.filter(d => (formData.shared_departments || []).includes(d.id)).map(d => ({
                            value: d.id,
                            label: d.branch_name ? `${d.name} (${d.branch_name})` : d.name,
                            data: d
                          }))}
                          onChange={handleDepartmentsChange}
                          isClearable
                          placeholder="Select Departments"
                          isLoading={loadingDepartments}
                          styles={customStyles}
                        />
                        <small className="text-muted" style={{ marginTop: '0.25rem', display: 'block' }}>
                          If none selected, only you will see this classification
                        </small>
                      </div>
                    </div>
                  )}
                </div>

                  <div className="text-end toolbar toolbar-bottom p-2">
                    <button 
                      type="button"
                      className="btn btn-cancel me-2" 
                      onClick={() => {
                        if (window.history.length > 2) {
                          navigate(-1);
                        } else {
                          navigate('/documents/documentClassifications');
                        }
                      }}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn user-form-btn sw-btn-next" 
                      onClick={handleSubmit} 
                      disabled={isSubmitting} 
                      style={{ maxWidth: '100px' }}
                    >
                      {loading ? (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      ) : null}
                      {editId ? "Update" : "Submit"}
                    </button>
                  </div>
              </section>
            </div>
          </div>
        </div>
        <InternalFooter />
      </div>
    </div>
  );
};

export default AddDocumentCategory;